import type { DiscoveryV2ListParametersType } from "@sellar-io/api-v2/types";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { useLocalStorage } from "usehooks-ts";
import { useShallow } from "zustand/react/shallow";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import {
  abvFilterType,
  useDiscoveryV2SearchStore,
} from "../DiscoveryV2SearchStore";
import { priceFilterType } from "../filterCards/FilterPriceTypes";
import { filterStyles } from "~/components/marketSearch/marketSearchFilters";

dayjs.extend(isoWeek);

const now = new Date();
const fridayOfCurrentWeek = dayjs(now).isoWeekday(5).endOf("day").toDate();

export const useDiscoveryV2SearchProductListParams = (options?: {
  limit?: number;
}): DiscoveryV2ListParametersType => {
  const {
    searchValue,
    styleSearchValues,
    categoryKey,
    selectedSuppliers,
    selectedContainerTypes,
    selectedDietaryTypes,
    selectedAbvTypes,
    selectedPriceTypes,
    isInStockItemsOnly,
    isMySuppliersOnly,
    isSellarCreditEligibleOnly,
    isDirectDelivery,
    isDeliveredBySellar,
    deliveredBySellarOption,
    orderCutOffAt,
  } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      searchValue: state.searchValue,
      styleSearchValues: state.styleSearchValues,
      categoryKey: state.categoryKey,
      selectedSuppliers: state.selectedSuppliers,
      selectedContainerTypes: state.selectedContainerTypes,
      selectedDietaryTypes: state.selectedDietaryTypes,
      selectedAbvTypes: state.selectedAbvTypes,
      selectedPriceTypes: state.selectedPriceTypes,
      isInStockItemsOnly: state.isInStockItemsOnly,
      isMySuppliersOnly: state.isMySuppliersOnly,
      isSellarCreditEligibleOnly: state.isSellarCreditEligibleOnly,
      isDirectDelivery: state.isDirectDelivery,
      isDeliveredBySellar: state.isDeliveredBySellar,
      deliveredBySellarOption: state.deliveredBySellarOption,
      orderCutOffAt: state.orderCutOffAt,
    })),
  );

  const { business } = useSellarCurrentBusiness();
  const [unAuthorisedUserPostcode] = useLocalStorage<string | undefined>(
    "unauthorisedUserPostcode",
    undefined,
  );
  const buyerPostcode =
    business?.type === "retailer" ? business.DeliveryAddress.postalCode : "";

  const styleSearchString = styleSearchValues.length
    ? `(${styleSearchValues.join("|")})`
    : "";
  const categorySearchString = categoryKey
    ? `(${filterStyles[categoryKey].searchValue})`
    : "";

  return {
    limit: options?.limit ?? 10,
    search: [searchValue, categorySearchString, styleSearchString]
      .filter(Boolean)
      .join(" & "),
    unAuthorisedUserPostcode,
    retailerPostcode: buyerPostcode,
    query: [
      ...(selectedContainerTypes.length
        ? ([
            {
              type: "containerType",
              modifier: "IN",
              value: Array.from(selectedContainerTypes.values()),
            },
          ] as const)
        : []),
      ...(selectedSuppliers.length
        ? ([
            {
              type: "supplierId",
              modifier: "IN",
              value: selectedSuppliers.map((supplier) => supplier.id),
            },
          ] as const)
        : []),
      ...(selectedDietaryTypes.length
        ? ([
            {
              type: "dietaryType",
              modifier: "IN",
              value: Array.from(selectedDietaryTypes.values()),
            },
          ] as const)
        : []),
      ...(selectedAbvTypes.length
        ? selectedAbvTypes.map((type) => abvFilterType[type].query)
        : []),
      ...(selectedPriceTypes.length
        ? selectedPriceTypes.map((type) => priceFilterType[type].query)
        : []),
      ...(isInStockItemsOnly
        ? ([
            {
              type: "inStockItemsOnly",
              modifier: "EQUALS",
              value: isInStockItemsOnly,
            },
          ] as const)
        : []),
      ...(isMySuppliersOnly
        ? ([
            {
              type: "mySuppliersOnly",
              modifier: "EQUALS",
              value: isMySuppliersOnly,
            },
          ] as const)
        : []),
      ...(isSellarCreditEligibleOnly
        ? ([
            {
              type: "sellarCreditEligibleOnly",
              modifier: "EQUALS",
              value: isSellarCreditEligibleOnly,
            },
          ] as const)
        : []),
      ...(isDirectDelivery
        ? ([
            {
              type: "directDelivery",
              modifier: "EQUALS",
              value: isDirectDelivery,
            },
          ] as const)
        : []),
      ...(isDeliveredBySellar
        ? ([
            {
              type: "deliveredBySellar",
              modifier: "EQUALS",
              value: isDeliveredBySellar,
            },
          ] as const)
        : []),
      ...(deliveredBySellarOption === "weekend"
        ? ([
            {
              type: "arrivingByDate",
              modifier: "BETWEEN",
              value: {
                low: now,
                high: fridayOfCurrentWeek,
              },
            },
          ] as const)
        : []),
      ...(orderCutOffAt
        ? ([
            {
              type: "orderCutOffAt",
              modifier: "BETWEEN",
              value: {
                low: dayjs(orderCutOffAt).toDate(),
                high: dayjs(orderCutOffAt).toDate(),
              },
            },
          ] as const)
        : []),
    ],
  };
};
